import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { useUpdateEffect } from '@tager/web-core';

import { useLockBodyScroll } from '@/hooks/useLockBodyScroll';
import CloseButton from '@/components/modals/shared/CloseButton';
import { useTypedDispatch } from '@/store/store';
import {
  getCreditInitialValuesThunk,
  getCreditProductsThunk,
  setCreditPeriod,
  setCreditPrepayment,
} from '@/store/reducers/detailed-car';
import Loader from '@/components/Loader';
import { media } from '@/utils/mixin';
import CarPreview from '@/components/CarPreview';
import { HideScrollbar } from '@/components/styles';
import { CreditType } from '@/typings/common';
import Tabs from '@/components/Tabs';

import { CreditModalProps } from './types';
import { defaultPrepayment, tabs } from './constants';
import Body from './components/Body';
import Inputs from './components/Inputs';
import CalcButton from './components/CalcButton';
import Note from './components/Note';
import Result from './components/Result';
import Products from './components/Products';

function CreditModal({ closeModal, innerProps }: CreditModalProps) {
  const {
    creditType,
    carType,
    carId,
    image,
    name,
    carViewBody,
    price,
    body,
    dealers,
    googleEventParams,
    revalidate = true,
  } = innerProps;

  const dispatch = useTypedDispatch();
  const [isLoading, setLoading] = useState(revalidate);
  const [isShowTabs, setIsShowTabs] = useState(true);
  const [currentCreditType, setCurrentCreditType] =
    useState<CreditType>(creditType);

  useLockBodyScroll();

  useEffect(() => {
    if (!revalidate) {
      return;
    }

    dispatch(setCreditPrepayment(defaultPrepayment));
    dispatch(setCreditPeriod(0));

    (async () => {
      try {
        const creditInitialValues = await dispatch(
          getCreditInitialValuesThunk(String(carId))
        );

        const { CREDIT, LEASING } = creditInitialValues;

        const convertedCreditType =
          currentCreditType === 'credit' ? 'CREDIT' : 'LEASING';

        if (!CREDIT) {
          setCurrentCreditType('leasing');
          setIsShowTabs(false);
        }
        if (!LEASING) {
          setIsShowTabs(false);
        }

        const defaultPeriod =
          creditInitialValues[convertedCreditType]?.defaultPeriod;

        await dispatch(
          getCreditProductsThunk({
            type: convertedCreditType,
            car: String(carId),
            prepayment: String(defaultPrepayment),
            period: String(defaultPeriod),
          })
        );
        dispatch(setCreditPeriod(defaultPeriod));

        setLoading(false);
      } catch (error) {
        console.log('error = ', error);
      }
    })();
  }, [currentCreditType]);

  useUpdateEffect(() => {
    setCurrentCreditType(creditType);
  }, [creditType]);

  return (
    <Component>
      <CloseButton onClick={closeModal} />

      {isLoading ? (
        <Loader />
      ) : (
        <Main>
          <Left>
            <Calculator>
              {isShowTabs && (
                <TabsWrapper>
                  <Tabs
                    data={tabs}
                    activeTab={currentCreditType}
                    onChange={setCurrentCreditType}
                    isWhiteBackground
                  />
                </TabsWrapper>
              )}

              <CarPreview
                image={image}
                name={name}
                price={price}
                currency="BYN"
                body={body}
                carType={carType}
              />
              <Body creditType={currentCreditType} body={carViewBody} />
              <Inputs creditType={currentCreditType} price={price} />
              <CalcButton creditType={currentCreditType} carId={carId} />
              <Note />
            </Calculator>
          </Left>

          <Right>
            <Result creditType={currentCreditType} />
            <Products
              creditType={currentCreditType}
              carType={carType}
              carId={carId}
              image={image}
              name={name}
              carViewBody={carViewBody}
              price={price}
              body={body}
              dealers={dealers}
              googleEventParams={googleEventParams}
            />
          </Right>
        </Main>
      )}
    </Component>
  );
}

export default CreditModal;

const Component = styled.div`
  min-height: 50px;
  width: calc(100vw - 20px);
  max-width: 1300px;
  border-radius: 10px;
  background: ${(props) => props.theme.white};

  ${HideScrollbar}

  ${media.tablet(css`
    max-height: none;
    overflow-y: initial;
  `)}
`;

const Main = styled.div`
  display: flex;

  ${media.tablet(css`
    flex-direction: column;
  `)}
`;

const Left = styled.div`
  flex: 1 1 50%;
  max-width: 50%;
  padding: 20px 20px 20px 30px;

  ${media.tablet(css`
    flex: auto;
    max-width: none;
    padding: 20px;
  `)}
`;

const Calculator = styled.div`
  position: sticky;
  top: 0;

  ${media.tablet(css`
    position: initial;
  `)}
`;

const Right = styled.div`
  flex: 1 1 50%;
  max-width: 50%;
  padding: 0 30px 20px 20px;

  ${media.tablet(css`
    flex: auto;
    max-width: none;
    padding: 0 20px 20px;
  `)}
`;

const TabsWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
`;
